import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { supabase } from '../supabaseClient';
import SEO from './SEO';

import '../assets/css/psearch.css';
import Nav from './Nav';

function FashionBoard() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('looks');
  const [showUserDropdown, setShowUserDropdown] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  
  // Saved data for the logged-in user
  const [savedLooks, setSavedLooks] = useState([]);
  const [savedProducts, setSavedProducts] = useState([]);
  const [savedBrands, setSavedBrands] = useState([]);
  // Optionally, add loading state for each
  const [looksLoading, setLooksLoading] = useState(false);
  const [productsLoading, setProductsLoading] = useState(false);
  const [brandsLoading, setBrandsLoading] = useState(false);

  const [previewLook, setPreviewLook] = useState(null);
  const [deletingLookId, setDeletingLookId] = useState(null);

  useEffect(() => {
    const getSessionAndSaved = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (session?.user) {
        setUser(session.user);
        setLooksLoading(true);
        try {
          const { data: looksData, error: looksError } = await supabase
            .from('saved_looks')
            .select('id, image, product_image, created_at')
            .eq('user_id', session.user.id)
            .order('created_at', { ascending: false });

          if (!looksError && looksData) {
            setSavedLooks(looksData);
          } else {
            setSavedLooks([]);
          }
        } catch (e) {
          setSavedLooks([]);
        } finally {
          setLooksLoading(false);
        }

        // Fetch saved products
        setProductsLoading(true);
        setBrandsLoading(true);
        try {
          // Fetch saved products (join with products table for details)
          const { data: savedProductIds, error: prodErr } = await supabase
            .from('saved_products')
            .select('product_id')
            .eq('user_id', session.user.id);

          let productDetails = [];
          if (savedProductIds && savedProductIds.length > 0) {
            // Get product details for each saved product
            const ids = savedProductIds.map(item => item.product_id);
            // Fetch all products in one query
            const { data: productsData, error: productsError } = await supabase
              .from('products')
              .select('*')
              .in('id', ids);
            if (!productsError && productsData) {
              productDetails = productsData;
            }
          }
          setSavedProducts(productDetails);
        } catch (e) {
          setSavedProducts([]);
        } finally {
          setProductsLoading(false);
        }

        // Fetch saved brands
        try {
          const { data: savedBrandRows, error: brandErr } = await supabase
            .from('saved_brands')
            .select('brand_name')
            .eq('user_id', session.user.id);
          // Optionally, fetch brand images from products table
          let brandDetails = [];
          if (savedBrandRows && savedBrandRows.length > 0) {
            const brandNames = savedBrandRows.map(item => item.brand_name);
            // Get one product per brand for image
            const { data: productsForBrands, error: prodBrandErr } = await supabase
              .from('products')
              .select('brand, image')
              .in('brand', brandNames);
            if (!prodBrandErr && productsForBrands) {
              // Group by brand
              const brandMap = {};
              for (const prod of productsForBrands) {
                if (!brandMap[prod.brand]) {
                  brandMap[prod.brand] = prod.image;
                }
              }
              brandDetails = brandNames.map(name => ({ name, image: brandMap[name] }));
            } else {
              brandDetails = brandNames.map(name => ({ name }));
            }
          }
          setSavedBrands(brandDetails);
        } catch (e) {
          setSavedBrands([]);
        } finally {
          setBrandsLoading(false);
        }
      } else {
        navigate('/');
      }
      setLoading(false);
    };

    getSessionAndSaved();
  }, [navigate]);

  const handleDeleteLook = async (lookId) => {
    if (!user?.id || !lookId) return;
    setDeletingLookId(lookId);
    try {
      const { error } = await supabase
        .from('saved_looks')
        .delete()
        .eq('id', lookId)
        .eq('user_id', user.id);
      if (!error) {
        setSavedLooks((prev) => prev.filter((l) => l.id !== lookId));
        if (previewLook?.id === lookId) {
          setPreviewLook(null);
        }
      }
    } finally {
      setDeletingLookId(null);
    }
  };

  const handleSignOut = async () => {
    await supabase.auth.signOut();
    navigate('/');
  };

  if (loading) {
    return (
      <div className="fb-loading">
        <div className="fb-spinner" />
        <style>{`
          .fb-loading { min-height: 100vh; display: flex; align-items: center; justify-content: center; background: #f9f7f5; }
          .fb-spinner { width: 32px; height: 32px; border: 2px solid #eee; border-top-color: #000; border-radius: 50%; animation: spin 0.8s linear infinite; }
          @keyframes spin { to { transform: rotate(360deg); } }
        `}</style>
      </div>
    );
  }

  const tabs = [
    { id: 'looks', label: 'Looks', count: savedLooks.length },
    { id: 'products', label: 'Products', count: savedProducts.length },
    { id: 'brands', label: 'Brands', count: savedBrands.length },
  ];

  const EmptyState = ({ title, description }) => (
    <div className="fb-empty">
      <div className="fb-empty-icon">
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#999" strokeWidth="1.5">
          <path d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
        </svg>
      </div>
      <h3 className="fb-empty-title">{title}</h3>
      <p className="fb-empty-desc">{description}</p>
      <Link to="/" className="fb-empty-btn">Start Exploring</Link>
    </div>
  );

  return (
    <div className="fb-page">
      <SEO title="Your Collection" path="/fashion-board" noindex={true} />
      <style>{`
        .fb-page {
          min-height: 100vh;
          background: #fff;
          font-family: 'Tan Aegean', serif;
        }
        /* Ensure Nav uses consistent font across all pages */
        .fb-page .yb-nav {
          font-family: 'Montserrat', sans-serif !important;
        }
        .fb-page .yb-nav-link {
          font-family: 'Montserrat', sans-serif !important;
        }
        .fb-shell {
          padding: 48px 0 56px;
        }
        .fb-container {
          max-width: 1000px;
          margin: 0 auto;
        }
        .fb-header {
          margin-bottom: 40px;
        }
        @media (max-width: 768px) {
          .fb-shell {
            padding: 24px 20px 40px;
          }
        }
        .fb-title {
          font-size: 32px;
          font-weight: 400;
          color: #000;
          margin: 0 0 8px 0;
        }
        .fb-subtitle {
          font-size: 14px;
          color: #888;
          margin: 0;
        }
        .fb-tabs {
          display: flex;
          gap: 0;
          margin-bottom: 32px;
          border-bottom: 1px solid #eee;
          background: #fff;
          // border-radius: 16px 16px 0 0;
          padding: 0 8px;
        }
        .fb-tab {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 18px 24px;
          background: transparent;
          color: #888;
          border: none;
          border-bottom: 2px solid transparent;
          font-size: 14px;
          font-family: inherit;
          cursor: pointer;
          transition: all 0.2s;
          margin-bottom: -1px;
        }
        .fb-tab:hover { color: #000; }
        .fb-tab.active {
          color: #000;
          border-bottom-color: #000;
        }
        .fb-tab-count {
          font-size: 12px;
          color: #ccc;
        }
        .fb-tab.active .fb-tab-count { color: #000; }
        .fb-content {
          background: #fff;
          border-radius: 0 0 16px 16px;
          padding: 32px;
          min-height: 400px;
          // border: 1px solid #eee;
          border-top: none;
        }
        .fb-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
          gap: 24px;
        }
        .fb-card {
          background: #fff;
          border-radius: 16px;
          overflow: hidden;
          border: 1px solid #eee;
          transition: all 0.3s ease;
        }
        .fb-card:hover {
          transform: translateY(-4px);
          box-shadow: 0 12px 32px rgba(0,0,0,0.08);
        }
        .fb-card-img {
          width: 100%;
          aspect-ratio: 3/4;
          object-fit: cover;
        }
        .fb-card-product {
          position: absolute;
          left: 12px;
          bottom: 60px;
          width: 48px;
          height: 48px;
          border-radius: 12px;
          overflow: hidden;
          border: 2px solid #fff;
          background: #fff;
          box-shadow: 0 4px 12px rgba(0,0,0,0.15);
        }
        .fb-card-product img {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }
        .fb-card-actions {
          position: absolute;
          left: 0;
          right: 0;
          bottom: 0;
          padding: 12px;
          display: flex;
          gap: 8px;
          background: linear-gradient(180deg, transparent 0%, rgba(0,0,0,0.6) 100%);
        }
        .fb-btn-preview {
          flex: 1;
          border: none;
          border-radius: 0px;
          padding: 12px;
          background: #fff;
          color: #000;
          font-size: 12px;
          font-family: inherit;
          cursor: pointer;
          transition: all 0.2s;
        }
        .fb-btn-preview:hover { background: #f9f7f5; }
        .fb-btn-delete {
          border: none;
          border-radius: 0px;
          padding: 12px 16px;
          background: #c00;
          color: #fff;
          font-size: 12px;
          font-family: inherit;
          cursor: pointer;
          transition: all 0.2s;
        }
        .fb-btn-delete:hover { background: #a00; }
        .fb-btn-delete:disabled { opacity: 0.6; cursor: not-allowed; }
        .fb-product-card {
          background: #fff;
          border-radius: 16px;
          overflow: hidden;
          border: 1px solid #eee;
          padding: 16px;
          cursor: pointer;
          transition: all 0.3s ease;
        }
        .fb-product-card:hover {
          transform: translateY(-4px);
          box-shadow: 0 12px 32px rgba(0,0,0,0.08);
        }
        .fb-product-img {
          width: 100%;
          aspect-ratio: 1/1;
          object-fit: cover;
          border-radius: 5px;
          margin-bottom: 16px;
        }
        .fb-product-brand {
          font-size: 12px;
          font-weight: 600;
          color: #000;
          margin: 0 0 4px 0;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }
        .fb-product-title {
          font-size: 13px;
          color: #888;
          margin: 0;
        }
        .fb-brand-card {
          background: #fff;
          border-radius: 0px;
          padding: 32px 20px;
          text-align: center;
          cursor: pointer;
          border: 1px solid #eee;
          transition: all 0.3s ease;
        }
        .fb-brand-card:hover {
          transform: translateY(-4px);
          box-shadow: 0 12px 32px rgba(0,0,0,0.08);
        }
        .fb-brand-img {
          width: 64px;
          height: 64px;
          object-fit: cover;
          border-radius: 50%;
          margin-bottom: 12px;
          border: 2px solid #f9f7f5;
        }
        .fb-brand-name {
          font-size: 14px;
          font-weight: 500;
          color: #000;
          margin: 0;
        }
        .fb-empty {
          text-align: center;
          padding: 80px 20px;
        }
        .fb-empty-icon {
          width: 64px;
          height: 64px;
          background: #f9f7f5;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          margin: 0 auto 24px;
        }
        .fb-empty-title {
          font-size: 18px;
          font-weight: 500;
          color: #000;
          margin: 0 0 8px 0;
        }
        .fb-empty-desc {
          font-size: 14px;
          color: #888;
          margin: 0 auto;
          max-width: 280px;
          line-height: 1.6;
        }
        .fb-empty-btn {
          display: inline-block;
          margin-top: 28px;
          padding: 14px 32px;
          background: #000;
          color: #fff;
          border-radius: 30px;
          text-decoration: none;
          font-size: 13px;
          text-transform: uppercase;
          letter-spacing: 1px;
          transition: all 0.2s;
        }
        .fb-empty-btn:hover { background: #333; }
        .fb-loading-text {
          text-align: center;
          padding: 40px;
          color: #888;
        }
      `}</style>

      <div className="container fb-shell">
      <Nav />

      <div className="fb-container">
        <div className="fb-header">
          <h1 className="fb-title">Your Collection</h1>
          <p className="fb-subtitle">Curated looks, products, and brands</p>
        </div>

        <div className="fb-tabs">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`fb-tab ${activeTab === tab.id ? 'active' : ''}`}
            >
              {tab.label}
              <span className="fb-tab-count">{tab.count}</span>
            </button>
          ))}
        </div>

        <div className="fb-content">
          {activeTab === 'looks' && (
            looksLoading ? (
              <div className="fb-loading-text">Loading your saved looks...</div>
            ) : savedLooks.length > 0 ? (
              <div className="fb-grid">
                {savedLooks.map((look, index) => (
                  <div key={look.created_at || index} className="fb-card" style={{ position: 'relative' }}>
                    <img
                      src={look.image}
                      alt={`Look ${index + 1}`}
                      className="fb-card-img"
                      onError={(e) => {
                        e.currentTarget.onerror = null;
                        e.currentTarget.src = '/default-image.avif';
                      }}
                    />
                    {look.product_image && (
                      <div className="fb-card-product">
                        <img
                          src={look.product_image}
                          alt="Product used"
                          onError={(e) => {
                            e.currentTarget.onerror = null;
                            e.currentTarget.src = '/default-image.avif';
                          }}
                        />
                      </div>
                    )}
                    <div className="fb-card-actions">
                      <button className="fb-btn-preview" onClick={() => setPreviewLook(look)}>
                        Preview
                      </button>
                      <button
                        className="fb-btn-delete"
                        onClick={() => handleDeleteLook(look.id)}
                        disabled={deletingLookId === look.id}
                      >
                        {deletingLookId === look.id ? '...' : 'Delete'}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <EmptyState
                title="No saved looks yet"
                description="Try on outfits and save your favorite generated looks here"
              />
            )
          )}

          {previewLook && (
            <div
              style={{
                position: 'fixed',
                inset: 0,
                background: 'rgba(10, 10, 10, 0.55)',
                backdropFilter: 'blur(10px)',
                zIndex: 10001,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '20px',
              }}
              onClick={() => setPreviewLook(null)}
            >
              <div
                style={{
                  background: '#fff',
                  borderRadius: '16px',
                  maxWidth: '760px',
                  width: '100%',
                  maxHeight: '78vh',
                  boxShadow: '0 24px 80px rgba(0,0,0,0.35)',
                  overflow: 'hidden',
                  border: '1px solid rgba(0,0,0,0.06)',
                }}
                onClick={(e) => e.stopPropagation()}
              >
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: '16px 18px',
                  borderBottom: '1px solid rgba(0,0,0,0.06)',
                }}>
                  <div>
                    <div style={{
                      fontSize: '13px',
                      fontWeight: 500,
                      color: '#111',
                      letterSpacing: '-0.2px',
                      lineHeight: 1.2,
                    }}>
                      Look preview
                    </div>
                    <div style={{
                      marginTop: '4px',
                      fontSize: '12px',
                      color: '#777',
                      fontWeight: 300,
                    }}>
                      Generated look and product used
                    </div>
                  </div>
                  <button
                    onClick={() => setPreviewLook(null)}
                    style={{
                      border: 'none',
                      background: '#f5f5f5',
                      fontSize: '16px',
                      cursor: 'pointer',
                      color: '#555',
                      width: '32px',
                      height: '32px',
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    ✕
                  </button>
                </div>

                <div style={{
                  display: 'grid',
                  gridTemplateColumns: previewLook.product_image ? '1fr 1fr' : '1fr',
                  gap: '0',
                  overflow: 'auto',
                  maxHeight: 'calc(78vh - 56px - 58px)',
                }}>
                  <div style={{ padding: '18px' }}>
                    <div style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '6px',
                      fontSize: '11px',
                      color: '#666',
                      marginBottom: '12px',
                      padding: '6px 10px',
                      borderRadius: '999px',
                      background: '#f7f7f7',
                      border: '1px solid rgba(0,0,0,0.06)',
                      width: 'fit-content',
                    }}>
                      Generated look
                    </div>
                    <div style={{
                      background: '#fafafa',
                      borderRadius: '12px',
                      border: '1px solid rgba(0,0,0,0.06)',
                      padding: '10px',
                    }}>
                      <img
                        src={previewLook.image}
                        alt="Saved look"
                        style={{
                          width: '100%',
                          borderRadius: '10px',
                          objectFit: 'contain',
                          display: 'block',
                        }}
                        onError={(e) => {
                          e.currentTarget.onerror = null;
                          e.currentTarget.src = '/default-image.avif';
                        }}
                      />
                    </div>
                  </div>

                  {previewLook.product_image && (
                    <div style={{ padding: '18px', borderLeft: '1px solid rgba(0,0,0,0.06)' }}>
                      <div style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '6px',
                        fontSize: '11px',
                        color: '#666',
                        marginBottom: '12px',
                        padding: '6px 10px',
                        borderRadius: '999px',
                        background: '#f7f7f7',
                        border: '1px solid rgba(0,0,0,0.06)',
                        width: 'fit-content',
                      }}>
                        Product used
                      </div>
                      <div style={{
                        background: '#fafafa',
                        borderRadius: '12px',
                        border: '1px solid rgba(0,0,0,0.06)',
                        padding: '10px',
                      }}>
                        <img
                          src={previewLook.product_image}
                          alt="Product used"
                          style={{
                            width: '100%',
                            borderRadius: '10px',
                            objectFit: 'contain',
                            display: 'block',
                          }}
                          onError={(e) => {
                            e.currentTarget.onerror = null;
                            e.currentTarget.src = '/default-image.avif';
                          }}
                        />
                      </div>
                    </div>
                  )}
                </div>

                <div style={{
                  padding: '14px 18px',
                  borderTop: '1px solid rgba(0,0,0,0.06)',
                  display: 'flex',
                  justifyContent: 'flex-end',
                  gap: '10px',
                }}>
                  <button
                    onClick={() => setPreviewLook(null)}
                    style={{
                      border: '1px solid rgba(0,0,0,0.10)',
                      background: '#fff',
                      borderRadius: '10px',
                      padding: '10px 14px',
                      fontSize: '12px',
                      cursor: 'pointer',
                      color: '#111',
                    }}
                  >
                    Close
                  </button>
                  <button
                    onClick={() => handleDeleteLook(previewLook.id)}
                    disabled={deletingLookId === previewLook.id}
                    style={{
                      border: 'none',
                      background: '#e63946',
                      borderRadius: '10px',
                      padding: '10px 14px',
                      fontSize: '12px',
                      cursor: deletingLookId === previewLook.id ? 'not-allowed' : 'pointer',
                      color: '#fff',
                      opacity: deletingLookId === previewLook.id ? 0.7 : 1,
                    }}
                  >
                    {deletingLookId === previewLook.id ? 'Deleting...' : 'Delete Look'}
                  </button>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'products' && (
            productsLoading ? (
              <div className="fb-loading-text">Loading your saved products...</div>
            ) : savedProducts.length > 0 ? (
              <div className="fb-grid">
                {savedProducts.map((product, index) => (
                  <div
                    key={product.id || index}
                    className="fb-product-card"
                    onClick={() => {
                      const brand = product.brand;
                      if (!brand) return;
                      const productId = product.id ?? product.link;
                      const query = productId ? `?productId=${encodeURIComponent(String(productId))}` : '';
                      navigate(`/brand/${encodeURIComponent(brand)}${query}`);
                    }}
                  >
                    <img
                      src={product.image}
                      alt={product.title}
                      className="fb-product-img"
                      onError={(e) => {
                        e.currentTarget.onerror = null;
                        e.currentTarget.src = '/default-image.avif';
                      }}
                    />
                    <p className="fb-product-brand yb-brand-name">{product.brand}</p>
                    <p className="fb-product-title">{product.title}</p>
                  </div>
                ))}
              </div>
            ) : (
              <EmptyState
                title="No saved products yet"
                description="Browse products and save your favorites to shop later"
              />
            )
          )}

          {activeTab === 'brands' && (
            brandsLoading ? (
              <div className="fb-loading-text">Loading your favorite brands...</div>
            ) : savedBrands.length > 0 ? (
              <div className="fb-grid">
                {savedBrands.map((brand, index) => (
                  <div
                    key={brand.name || index}
                    className="fb-brand-card"
                    onClick={() => {
                      if (!brand?.name) return;
                      navigate(`/brand/${encodeURIComponent(brand.name)}`);
                    }}
                  >
                    {brand.image && (
                      <img
                        src={brand.image}
                        alt={brand.name}
                        className="fb-brand-img"
                        onError={(e) => {
                          e.currentTarget.onerror = null;
                          e.currentTarget.src = '/default-image.avif';
                        }}
                      />
                    )}
                    <p className="fb-brand-name yb-brand-name">{brand.name}</p>
                  </div>
                ))}
              </div>
            ) : (
              <EmptyState
                title="No favorite brands yet"
                description="Discover Kashmiri brands and save your favorites"
              />
            )
          )}
        </div>       
      </div>
      </div>
    </div>
  );
}

export default FashionBoard;
